import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { authedAtTimestampAtom } from '../../auth/state';
import { useActiveBrand } from '../../components/branding/useActiveBrand';
import { logger } from '../../datadog';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { sessionInfoAtom } from '../../state';
import {
  VisitorState,
  clientRequiresSafetyGuardsAtom,
  experiencedHardBlockThisSessionAtom,
  hasEvaluatedSoftBlockEligibilityAtom,
  isPayorExcludedFromSoftBlockAtom,
  isSoftBlockResolvedAtom,
  staleSoftBlockMeasureTypesAtom,
  visitorNameAtom,
  visitorStateAtom,
} from '../state';
import { setDefaultWaitingRoomFaviconAndTitle } from '../waiting-room/metadata';

export function useEnterWaitingRoomCallback() {
  const activeBrand = useActiveBrand();
  const { patientShortId, providerShortId } = useParams();
  const { userInputted } = useTrackEvent();
  const setVisitorName = useSetAtom(visitorNameAtom);
  const setVisitorState = useSetAtom(visitorStateAtom);
  const getAuthedAtTimestamp = useAtomCallbackMemoized((get) => get(authedAtTimestampAtom), []);
  const getSoftBlockInfo = useAtomCallbackMemoized((get) => get(sessionInfoAtom)?.softBlock, []);
  const getIsSoftBlockResolved = useAtomCallbackMemoized((get) => get(isSoftBlockResolvedAtom), []);
  const getStaleMeasureTypes = useAtomCallbackMemoized((get) => get(staleSoftBlockMeasureTypesAtom), []);
  const getExperiencedHardBlockThisSession = useAtomCallbackMemoized(
    (get) => get(experiencedHardBlockThisSessionAtom),
    [],
  );
  const getClientRequiresSafetyGuards = useAtomCallbackMemoized((get) => get(clientRequiresSafetyGuardsAtom), []);
  const getIsPayorExcludedFromSoftBlock = useAtomCallbackMemoized((get) => get(isPayorExcludedFromSoftBlockAtom), []);
  // Read-and-set the once-per-session guard atomically so the eligibility event
  // fires only on the first (determining) routing evaluation.
  const markEligibilityEvaluatedOnce = useAtomCallbackMemoized((get, set) => {
    if (get(hasEvaluatedSoftBlockEligibilityAtom)) return false;
    set(hasEvaluatedSoftBlockEligibilityAtom, true);
    return true;
  }, []);

  return useCallback(
    (visitorName: string) => {
      const isAuthenticated = getAuthedAtTimestamp() !== null;
      const isServerEligible = getSoftBlockInfo()?.isServerEligible ?? false;
      const staleMeasureTypes = getStaleMeasureTypes() ?? [];
      const experiencedHardBlock = getExperiencedHardBlockThisSession();
      const wasAlreadyResolved = getIsSoftBlockResolved();
      // Under-13 clients (COPPA) require safety guards and must never be
      // soft-blocked. Treat an unresolved value as "requires guards" so we err on
      // the side of excluding a possible minor under 13.
      const isWithinAgeThreshold = !(getClientRequiresSafetyGuards() ?? true);
      // Excluded-payor clients (e.g. KPNC) must never be soft-blocked. Treat an
      // unresolved value as "excluded" so we err on the side of not soft-blocking
      // when the payor exclusion is unknown.
      const isPayorExcluded = getIsPayorExcludedFromSoftBlock() ?? true;
      // Fail open: an unresolved (undefined) or empty stale-measures result keeps
      // the client out of the soft block. We never show it without stale measures.
      const hasStaleMeasures = staleMeasureTypes.length > 0;
      const isEligibleForSoftBlock =
        isAuthenticated &&
        isServerEligible &&
        hasStaleMeasures &&
        !experiencedHardBlock &&
        isWithinAgeThreshold &&
        !isPayorExcluded;

      const shouldSoftBlock = isEligibleForSoftBlock && !wasAlreadyResolved;

      // Fire the eligibility event only on the first (determining) routing
      // evaluation, not on every subsequent enterWaitingRoom call (e.g. once the
      // soft block is resolved via skip / completion / auto-transition).
      if (markEligibilityEvaluatedOnce()) {
        userInputted(EventTypeEnum.SOFT_BLOCK_ELIGIBILITY_EVALUATED, {
          patientShortId,
          providerShortId,
          isEligible: isEligibleForSoftBlock,
          isServerEligible,
          isAuthenticated,
          hasStaleMeasures,
          staleMeasureTypes,
          experiencedHardBlock,
          isWithinAgeThreshold,
          isPayorExcluded,
          wasAlreadyResolved,
        });

        logger.info('Soft-block eligibility evaluated', {
          feature: 'soft_block',
          patientShortId,
          providerShortId,
          isEligible: isEligibleForSoftBlock,
          isServerEligible,
          isAuthenticated,
          hasStaleMeasures,
          staleMeasureTypes,
          experiencedHardBlock,
          isWithinAgeThreshold,
          isPayorExcluded,
          wasAlreadyResolved,
          shouldSoftBlock,
        });
      }

      setVisitorName(visitorName);

      if (shouldSoftBlock) {
        return setVisitorState(VisitorState.PRESESSION_MEASURES);
      }

      setVisitorState(VisitorState.WAITING);
      setDefaultWaitingRoomFaviconAndTitle(activeBrand);
    },
    [
      activeBrand,
      patientShortId,
      providerShortId,
      userInputted,
      markEligibilityEvaluatedOnce,
      setVisitorName,
      setVisitorState,
      getAuthedAtTimestamp,
      getSoftBlockInfo,
      getIsSoftBlockResolved,
      getStaleMeasureTypes,
      getExperiencedHardBlockThisSession,
      getClientRequiresSafetyGuards,
      getIsPayorExcludedFromSoftBlock,
    ],
  );
}
